import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Row,
  Col,
  Card,
  Typography,
  Input,
  Spin,
  Empty,
  Pagination,
  Tag,
  Space,
  Segmented,
  Button,
  Tooltip,
  message,
} from 'antd';
import {
  CalendarOutlined,
  EnvironmentOutlined,
  TeamOutlined,
  StarFilled,
  SearchOutlined,
  HeartOutlined,
  HeartFilled,
  FireOutlined,
  AppstoreOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import PagesWrapper from '../../../PagesWrapper';
import * as eventsService from '../../../services/eventsService';
import { getFileUrl } from '../../../utils/urlUtils';
import styles from './EventsPublic.module.css';

const { Title, Text, Paragraph } = Typography;
const { Search } = Input;

// Constants for tabs
const TAB_ALL = 'all';
const TAB_FEATURED = 'featured';
const TAB_SAVED = 'saved';

// Local storage key for saved events
const SAVED_EVENTS_KEY = 'heatcraft_saved_events';

/**
 * Custom hook for managing saved events in localStorage
 */
const useSavedEvents = () => {
  const [savedEventIds, setSavedEventIds] = useState(() => {
    try {
      const stored = localStorage.getItem(SAVED_EVENTS_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  const saveEvent = useCallback((eventId) => {
    setSavedEventIds(prev => {
      const updated = [...new Set([...prev, eventId])];
      localStorage.setItem(SAVED_EVENTS_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const unsaveEvent = useCallback((eventId) => {
    setSavedEventIds(prev => {
      const updated = prev.filter(id => id !== eventId);
      localStorage.setItem(SAVED_EVENTS_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const isEventSaved = useCallback((eventId) => {
    return savedEventIds.includes(eventId);
  }, [savedEventIds]);

  const toggleSaveEvent = useCallback((eventId) => {
    if (isEventSaved(eventId)) {
      unsaveEvent(eventId);
      return false;
    } else {
      saveEvent(eventId);
      return true;
    }
  }, [isEventSaved, saveEvent, unsaveEvent]);

  return { savedEventIds, saveEvent, unsaveEvent, isEventSaved, toggleSaveEvent };
};

/**
 * Placeholder SVG for events without banner images
 */
const EventPlaceholderSVG = () => (
  <svg 
    viewBox="0 0 400 225" 
    className={styles.placeholderSvg}
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <linearGradient id="bgGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#1890ff" />
        <stop offset="100%" stopColor="#096dd9" />
      </linearGradient>
    </defs>
    {/* Background */}
    <rect width="400" height="225" fill="url(#bgGradient)" />
    {/* Calendar icon */}
    <g transform="translate(160, 72)">
      <rect x="0" y="10" width="80" height="70" rx="8" fill="rgba(255,255,255,0.95)" />
      <rect x="0" y="10" width="80" height="20" rx="8" fill="#1890ff" />
      <rect x="20" y="0" width="8" height="16" rx="2" fill="rgba(255,255,255,0.9)" />
      <rect x="52" y="0" width="8" height="16" rx="2" fill="rgba(255,255,255,0.9)" />
      <circle cx="24" cy="50" r="5" fill="#1890ff" opacity="0.6" />
      <circle cx="40" cy="50" r="5" fill="#096dd9" />
      <circle cx="56" cy="50" r="5" fill="#1890ff" opacity="0.6" />
      <rect x="16" y="62" width="48" height="4" rx="2" fill="#e0e0e0" />
    </g>
  </svg>
);

const EventsPublic = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Saved events hook
  const { savedEventIds, isEventSaved, toggleSaveEvent } = useSavedEvents();

  // State
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || TAB_ALL);
  const [loading, setLoading] = useState(true);
  const [allEvents, setAllEvents] = useState([]);
  const [featuredEvents, setFeaturedEvents] = useState([]);
  const [savedEventsData, setSavedEventsData] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 12,
    total: 0,
  });
  const [featuredPagination, setFeaturedPagination] = useState({
    current: 1,
    pageSize: 12,
    total: 0,
  });
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch all events (Tab: All)
  const fetchAllEvents = useCallback(async (page = 1, search = '') => {
    setLoading(true);
    try {
      const response = await eventsService.getPublicEvents({
        page,
        pageSize: pagination.pageSize,
        search: search || undefined,
        upcoming: true,
      });
      setAllEvents(response.items || []);
      setPagination(prev => ({
        ...prev,
        current: response.page,
        total: response.totalCount,
      }));
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.pageSize]);

  // Fetch featured events with weighted sorting (Tab: Featured)
  const fetchFeaturedEvents = useCallback(async (page = 1, search = '') => {
    setLoading(true);
    try {
      // Fetch featured/trending events (API returns only featured events)
      const trendingData = await eventsService.getTrendingEvents(50);
      let events = trendingData || [];
      
      // Apply search filter if provided
      if (search) {
        const searchLower = search.toLowerCase();
        events = events.filter(event => 
          event.title?.toLowerCase().includes(searchLower) ||
          event.location?.toLowerCase().includes(searchLower) ||
          event.purpose?.toLowerCase().includes(searchLower) ||
          event.organization?.toLowerCase().includes(searchLower)
        );
      }

      // Calculate weighted score for sorting
      // Weight = (isFeatured * 1000) + (registrationCount * 2) + (recency factor)
      const now = dayjs();
      events = events.map(event => {
        const daysUntilEvent = dayjs(event.startDateTime).diff(now, 'day');
        const recencyScore = Math.max(0, 100 - daysUntilEvent * 2); // Closer events score higher
        const popularityScore = (event.registrationCount || 0) * 2;
        const featuredBonus = event.isFeatured ? 1000 : 0;
        return {
          ...event,
          _score: featuredBonus + popularityScore + recencyScore,
        };
      }).sort((a, b) => b._score - a._score);

      // Paginate locally
      const startIndex = (page - 1) * featuredPagination.pageSize;
      const paginatedEvents = events.slice(startIndex, startIndex + featuredPagination.pageSize);

      setFeaturedEvents(paginatedEvents);
      setFeaturedPagination(prev => ({
        ...prev,
        current: page,
        total: events.length,
      }));
    } catch (error) {
      console.error('Failed to fetch featured events:', error);
    } finally {
      setLoading(false);
    }
  }, [featuredPagination.pageSize]);

  // Fetch saved events data (Tab: Saved)
  const fetchSavedEventsData = useCallback(async (search = '') => {
    if (savedEventIds.length === 0) {
      setSavedEventsData([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // Fetch all events and filter by saved IDs
      const response = await eventsService.getPublicEvents({
        page: 1,
        pageSize: 100,
        upcoming: false, // Include past events that user saved
      });
      
      let events = (response.items || []).filter(event => 
        savedEventIds.includes(event.id)
      );

      // Apply search filter
      if (search) {
        const searchLower = search.toLowerCase();
        events = events.filter(event => 
          event.title?.toLowerCase().includes(searchLower) ||
          event.location?.toLowerCase().includes(searchLower) ||
          event.purpose?.toLowerCase().includes(searchLower)
        );
      }

      // Sort by start date descending
      events.sort((a, b) => new Date(b.startDateTime) - new Date(a.startDateTime));

      setSavedEventsData(events);
    } catch (error) {
      console.error('Failed to fetch saved events:', error);
    } finally {
      setLoading(false);
    }
  }, [savedEventIds]);

  // Initial fetch and tab change handler
  useEffect(() => {
    const search = debouncedSearchTerm;
    
    if (activeTab === TAB_ALL) {
      fetchAllEvents(1, search);
    } else if (activeTab === TAB_FEATURED) {
      fetchFeaturedEvents(1, search);
    } else if (activeTab === TAB_SAVED) {
      fetchSavedEventsData(search);
    }
  }, [activeTab, debouncedSearchTerm, fetchAllEvents, fetchFeaturedEvents, fetchSavedEventsData]);

  // Update URL when tab or search changes
  useEffect(() => {
    const params = {};
    if (activeTab !== TAB_ALL) params.tab = activeTab;
    if (searchTerm) params.search = searchTerm;
    setSearchParams(params);
  }, [activeTab, searchTerm, setSearchParams]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPagination(prev => ({ ...prev, current: 1 }));
    setFeaturedPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
  };

  const handlePageChange = (page) => {
    if (activeTab === TAB_ALL) {
      fetchAllEvents(page, debouncedSearchTerm);
    } else if (activeTab === TAB_FEATURED) {
      fetchFeaturedEvents(page, debouncedSearchTerm);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleEventClick = (eventId) => {
    navigate(`/events/${eventId}`);
  };

  const handleSaveClick = (e, eventId) => {
    e.stopPropagation();
    const isSaved = toggleSaveEvent(eventId);
    if (isSaved) {
      message.success(t('events.savedToList', 'Event saved to your list'));
    } else {
      message.info(t('events.removedFromList', 'Event removed from your list'));
    }
  };

  const formatEventDate = (startDate, endDate) => {
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    
    if (start.isSame(end, 'day')) {
      return `${start.format('DD MMM YYYY')} • ${start.format('HH:mm')} - ${end.format('HH:mm')}`;
    }
    return `${start.format('DD MMM')} - ${end.format('DD MMM YYYY')}`;
  };

  const getTimeUntilEvent = (startDate) => {
    const start = dayjs(startDate);
    const now = dayjs();
    const diff = start.diff(now, 'day');
    
    if (diff < 0) return null;
    if (diff === 0) return t('events.today', 'Today');
    if (diff === 1) return t('events.tomorrow', 'Tomorrow');
    if (diff <= 7) return t('events.inDays', 'In {{count}} days', { count: diff });
    if (diff <= 30) return t('events.inWeeks', 'In {{count}} weeks', { count: Math.ceil(diff / 7) });
    return null;
  };

  // Tab options for Segmented control
  const tabOptions = useMemo(() => [
    {
      value: TAB_ALL,
      label: (
        <span className={styles.tabLabel}>
          <AppstoreOutlined /> {t('events.tabs.all', 'All Events')}
        </span>
      ),
    },
    {
      value: TAB_FEATURED,
      label: (
        <span className={styles.tabLabel}>
          <FireOutlined /> {t('events.tabs.featured', 'Featured')}
        </span>
      ),
    },
    {
      value: TAB_SAVED,
      label: (
        <span className={styles.tabLabel}>
          <HeartFilled /> {t('events.tabs.saved', 'Saved')}
          {savedEventIds.length > 0 && (
            <span className={styles.tabBadge}>{savedEventIds.length}</span>
          )}
        </span>
      ),
    },
  ], [t, savedEventIds.length]);

  // Get current events based on active tab
  const currentEvents = useMemo(() => {
    switch (activeTab) {
      case TAB_FEATURED:
        return featuredEvents;
      case TAB_SAVED:
        return savedEventsData;
      default:
        return allEvents;
    }
  }, [activeTab, allEvents, featuredEvents, savedEventsData]);

  const currentPagination = activeTab === TAB_FEATURED ? featuredPagination : pagination;
  const showPagination = activeTab !== TAB_SAVED && currentPagination.total > currentPagination.pageSize;

  const EventCard = ({ event, featured = false }) => {
    const saved = isEventSaved(event.id);
    const timeUntil = getTimeUntilEvent(event.startDateTime);

    return (
      <Card
        hoverable
        className={`${styles.eventCard} ${featured ? styles.featuredCard : ''} ${saved ? styles.savedCard : ''}`}
        onClick={() => handleEventClick(event.id)}
        cover={
          <div className={styles.cardCover}>
            {event.bannerImageUrl ? (
              <img 
                src={getFileUrl(event.bannerImageUrl)} 
                alt={event.title} 
                className={styles.coverImage}
              />
            ) : (
              <EventPlaceholderSVG />
            )}
            <div className={styles.cardOverlay}>
              {(featured || event.isFeatured) && (
                <Tag color="gold" className={styles.featuredTag}>
                  <StarFilled /> {t('events.featured', 'Featured')}
                </Tag>
              )}
              {timeUntil && (
                <Tag color="blue" className={styles.timeTag}>
                  <ClockCircleOutlined /> {timeUntil}
                </Tag>
              )}
            </div>
            <Tooltip title={saved ? t('events.unsave', 'Remove from saved') : t('events.save', 'Save event')}>
              <Button
                type="text"
                className={`${styles.saveButton} ${saved ? styles.savedButton : ''}`}
                icon={saved ? <HeartFilled /> : <HeartOutlined />}
                onClick={(e) => handleSaveClick(e, event.id)}
              />
            </Tooltip>
          </div>
        }
      >
        <div className={styles.cardContent}>
          <Title level={5} className={styles.cardTitle} ellipsis={{ rows: 2 }}>
            {event.title}
          </Title>
          
          <Space direction="vertical" size={4} className={styles.cardMeta}>
            <Text type="secondary" className={styles.metaItem}>
              <CalendarOutlined /> {formatEventDate(event.startDateTime, event.endDateTime)}
            </Text>
            <Text type="secondary" className={styles.metaItem}>
              <EnvironmentOutlined /> {event.location}
            </Text>
            {event.registrationCount > 0 && (
              <Text type="secondary" className={styles.metaItem}>
                <TeamOutlined /> {t('events.registrations', '{{count}} registered', { count: event.registrationCount })}
              </Text>
            )}
          </Space>

          {event.purpose && (
            <Paragraph type="secondary" ellipsis={{ rows: 2 }} className={styles.cardPurpose}>
              {event.purpose}
            </Paragraph>
          )}
        </div>
      </Card>
    );
  };

  const renderEmptyState = () => {
    if (activeTab === TAB_SAVED) {
      if (savedEventIds.length === 0) {
        return (
          <Empty
            image={<HeartOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />}
            description={
              <Space direction="vertical" size={8}>
                <Text type="secondary">{t('events.noSavedEvents', 'No saved events yet')}</Text>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  {t('events.noSavedEventsHint', 'Click the heart icon on any event to save it for later')}
                </Text>
              </Space>
            }
          >
            <Button type="primary" onClick={() => setActiveTab(TAB_ALL)}>
              {t('events.browseEvents', 'Browse Events')}
            </Button>
          </Empty>
        );
      }
      if (searchTerm && savedEventsData.length === 0) {
        return (
          <Empty description={t('events.noSearchResults', 'No events found matching your search')} />
        );
      }
    }

    return (
      <Empty
        description={
          searchTerm
            ? t('events.noSearchResults', 'No events found matching your search')
            : t('events.noEvents', 'No upcoming events at this time')
        }
      />
    );
  };

  return (
    <PagesWrapper>
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.pageHeader}>
          <CalendarOutlined className={styles.headerIcon} />
          <Title level={2} className={styles.pageTitle}>
            {t('events.publicTitle', 'Upcoming Events')}
          </Title>
          <Text type="secondary" className={styles.pageSubtitle}>
            {t('events.publicSubtitle', 'Discover and register for upcoming heat pump training events')}
          </Text>
        </div>

        {/* Search & Filter Bar */}
        <div className={styles.controlsBar}>
          <div className={styles.searchWrapper}>
            <Search
              placeholder={t('events.searchPlaceholder', 'Search events by title, location, or topic...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onSearch={handleSearch}
              allowClear
              prefix={<SearchOutlined className={styles.searchIcon} />}
              size="large"
              className={styles.searchInput}
            />
          </div>
          
          <Segmented
            value={activeTab}
            onChange={handleTabChange}
            options={tabOptions}
            size="large"
            className={styles.tabSegmented}
          />
        </div>

        {/* Events Grid */}
        <section className={styles.eventsSection}>
          <div className={styles.sectionHeader}>
            <Title level={4} className={styles.sectionTitle}>
              {activeTab === TAB_ALL && t('events.allEvents', 'All Events')}
              {activeTab === TAB_FEATURED && t('events.featuredEvents', 'Featured Events')}
              {activeTab === TAB_SAVED && t('events.savedEvents', 'Saved Events')}
              {currentEvents.length > 0 && (
                <Text type="secondary" className={styles.eventCount}>
                  ({activeTab === TAB_SAVED ? savedEventsData.length : (activeTab === TAB_FEATURED ? featuredPagination.total : pagination.total)})
                </Text>
              )}
            </Title>
          </div>

          {loading ? (
            <div className={styles.loadingContainer}>
              <Spin size="large" />
            </div>
          ) : currentEvents.length === 0 ? (
            <div className={styles.emptyContainer}>
              {renderEmptyState()}
            </div>
          ) : (
            <>
              <Row gutter={[20, 20]}>
                {currentEvents.map((event) => (
                  <Col xs={24} sm={12} md={8} lg={6} key={event.id}>
                    <EventCard event={event} featured={activeTab === TAB_FEATURED} />
                  </Col>
                ))}
              </Row>

              {showPagination && (
                <div className={styles.paginationContainer}>
                  <Pagination
                    current={currentPagination.current}
                    pageSize={currentPagination.pageSize}
                    total={currentPagination.total}
                    onChange={handlePageChange}
                    showSizeChanger={false}
                    showTotal={(total, range) => 
                      t('events.paginationInfo', '{{start}}-{{end}} of {{total}} events', {
                        start: range[0],
                        end: range[1],
                        total,
                      })
                    }
                  />
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </PagesWrapper>
  );
};

export default EventsPublic;
